/**
 * Deterministic natural-language quick-add parser (NO AI / NO LLM).
 *
 * Given a raw quick-add string such as
 *   "Buy milk tomorrow 6pm #home"
 * it extracts:
 *   - a due date (natural-language dates + times in English),
 *   - a list/tag token (`#tag` or `@list`),
 * and returns a cleaned title with those tokens stripped out.
 *
 * The parser is intentionally dependency-free and fully deterministic so it is
 * trivially unit-testable and behaves identically across web/desktop/mobile.
 * A `now` reference can be injected for testing.
 */

const HOURS_IN_HALF_DAY = 12;
const MIN_PM_HOUR = 1;
const MAX_12H = 12;
const MAX_MINUTE = 59;
const DAYS_IN_WEEK = 7;
const DEFAULT_DUE_HOUR = 9; // 9:00 local time when a date has no explicit time
const MAX_24H = 23;

/** A token detected in the raw input, with its position so we can strip it. */
interface Match {
  start: number;
  end: number;
}

export interface ParsedQuickAdd {
  /** Title with all recognized tokens removed and whitespace collapsed. */
  title: string;
  /** ISO 8601 due date/time, or null when no date was detected. */
  dueDate: string | null;
  /** Whether the detected date carried an explicit clock time. */
  hasTime: boolean;
  /** The list/tag name from `#tag` or `@list`, or null. */
  listToken: string | null;
  /** Human-readable summary of the matched date span (for the preview UI). */
  dateLabel: string | null;
}

const WEEKDAYS: Record<string, number> = {
  sunday: 0,
  sun: 0,
  monday: 1,
  mon: 1,
  tuesday: 2,
  tue: 2,
  tues: 2,
  wednesday: 3,
  wed: 3,
  thursday: 4,
  thu: 4,
  thur: 4,
  thurs: 4,
  friday: 5,
  fri: 5,
  saturday: 6,
  sat: 6,
};

const UNIT_DAYS: Record<string, number> = {
  day: 1,
  days: 1,
  week: 7,
  weeks: 7,
};

/** Clamp helper. */
function inRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}

/** Builds a Date at the given local Y/M/D with optional H:M. */
function makeDate(base: Date, hour: number, minute: number): Date {
  const d = new Date(base);
  d.setHours(hour, minute, 0, 0);
  return d;
}

interface TimeResult {
  hour: number;
  minute: number;
  match: Match;
}

/**
 * Finds an explicit clock time anywhere in the lowercased string:
 * "6pm", "6:30pm", "18:00", "6 pm", "at 6pm", "noon", "midnight".
 */
function findTime(lower: string): TimeResult | null {
  // noon / midnight keywords
  const keyword = /\b(noon|midnight)\b/.exec(lower);
  if (keyword) {
    const hour = keyword[1] === "noon" ? HOURS_IN_HALF_DAY : 0;
    return {
      hour,
      minute: 0,
      match: { start: keyword.index, end: keyword.index + keyword[0].length },
    };
  }

  // Optional leading "at ", then H or H:MM, then optional am/pm.
  const re = /\b(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(lower)) !== null) {
    const rawHour = Number(m[1]);
    const minute = m[2] ? Number(m[2]) : 0;
    const meridiem = m[3]?.toLowerCase();

    if (!inRange(minute, 0, MAX_MINUTE)) {
      continue;
    }

    // With am/pm we accept 1..12; without, only a valid 24h hour AND it must
    // either contain a colon or an explicit am/pm to avoid eating bare numbers
    // like "buy 3 eggs".
    if (meridiem) {
      if (!inRange(rawHour, MIN_PM_HOUR, MAX_12H)) {
        continue;
      }
      let hour = rawHour % HOURS_IN_HALF_DAY;
      if (meridiem === "pm") {
        hour += HOURS_IN_HALF_DAY;
      }
      return { hour, minute, match: { start: m.index, end: m.index + m[0].length } };
    }

    // No meridiem: require a colon (e.g. "18:00" / "6:30") to be considered a time.
    if (m[2] !== undefined && inRange(rawHour, 0, MAX_24H)) {
      return {
        hour: rawHour,
        minute,
        match: { start: m.index, end: m.index + m[0].length },
      };
    }
  }
  return null;
}

interface DateResult {
  /** Local Y/M/D anchor (time not yet applied). */
  date: Date;
  match: Match;
  label: string;
}

/** Finds a natural-language calendar date (no time component). */
function findDate(lower: string, now: Date): DateResult | null {
  // today / tomorrow / tonight
  const simple = /\b(today|tonight|tomorrow|tmr|tmrw)\b/.exec(lower);
  if (simple) {
    const word = simple[1] ?? "";
    const date = new Date(now);
    if (word === "tomorrow" || word === "tmr" || word === "tmrw") {
      date.setDate(date.getDate() + 1);
    }
    return {
      date,
      match: { start: simple.index, end: simple.index + simple[0].length },
      label: word,
    };
  }

  // "in 3 days" / "in 2 weeks"
  const relative = /\bin\s+(\d{1,3})\s+(days?|weeks?)\b/.exec(lower);
  if (relative) {
    const amount = Number(relative[1]);
    const unitKey = relative[2] ?? "";
    const unit = UNIT_DAYS[unitKey] ?? 1;
    const date = new Date(now);
    date.setDate(date.getDate() + amount * unit);
    return {
      date,
      match: { start: relative.index, end: relative.index + relative[0].length },
      label: relative[0],
    };
  }

  // "next monday" / "this fri" / bare "monday"
  const weekdayNames = Object.keys(WEEKDAYS).join("|");
  const wdRe = new RegExp(`\\b(next\\s+|this\\s+)?(${weekdayNames})\\b`, "i");
  const wd = wdRe.exec(lower);
  if (wd) {
    const target = WEEKDAYS[(wd[2] ?? "").toLowerCase()];
    if (target !== undefined) {
      const date = new Date(now);
      let delta = (target - date.getDay() + DAYS_IN_WEEK) % DAYS_IN_WEEK;
      // Both bare ("monday") and the "next"/"this" prefixes resolve to the next
      // future occurrence; a weekday equal to today rolls to next week so the
      // due date never lands in the past.
      if (delta === 0) {
        delta = DAYS_IN_WEEK;
      }
      date.setDate(date.getDate() + delta);
      return {
        date,
        match: { start: wd.index, end: wd.index + wd[0].length },
        label: wd[0].trim(),
      };
    }
  }

  return null;
}

/** Finds a single `#tag` or `@list` token. First one wins. */
function findListToken(raw: string): { name: string; match: Match } | null {
  const re = /(^|\s)([#@])([\p{L}\p{N}_-]+)/u;
  const m = re.exec(raw);
  if (m?.[3] === undefined) {
    return null;
  }
  const leading = m[1] ?? "";
  const tokenStart = m.index + leading.length;
  return {
    name: m[3],
    match: { start: tokenStart, end: m.index + m[0].length },
  };
}

/** Removes the given (possibly overlapping) spans from the raw string. */
function stripSpans(raw: string, spans: Match[]): string {
  if (spans.length === 0) {
    return raw.trim();
  }
  const sorted = [...spans].toSorted((a, b) => a.start - b.start);
  let out = "";
  let cursor = 0;
  for (const span of sorted) {
    // Emit the untouched text between the cursor and this span (if any),
    // tolerating overlapping spans by never moving the cursor backwards.
    if (span.start > cursor) {
      out += raw.slice(cursor, span.start);
    }
    cursor = Math.max(cursor, span.end);
  }
  out += raw.slice(cursor);
  // Collapse whitespace left behind by removed tokens.
  return out.replaceAll(/\s+/g, " ").trim();
}

/**
 * Parses a raw quick-add string into structured fields. Deterministic; pass
 * `now` to control the reference time (defaults to current time).
 */
export function parseQuickAdd(raw: string, now: Date = new Date()): ParsedQuickAdd {
  const lower = raw.toLowerCase();
  const spans: Match[] = [];

  const listToken = findListToken(raw);
  if (listToken) {
    spans.push(listToken.match);
  }

  const dateMatch = findDate(lower, now);
  const timeMatch = findTime(lower);

  let dueDate: string | null = null;
  let hasTime = false;
  let dateLabel: string | null = null;

  if (dateMatch || timeMatch) {
    const anchor = dateMatch ? dateMatch.date : new Date(now);
    const hour = timeMatch ? timeMatch.hour : DEFAULT_DUE_HOUR;
    const minute = timeMatch ? timeMatch.minute : 0;
    const due = makeDate(anchor, hour, minute);

    // A bare time ("6pm") with no date means "today", but if that time already
    // passed, roll to tomorrow so the due date is in the future.
    if (!dateMatch && timeMatch && due.getTime() <= now.getTime()) {
      due.setDate(due.getDate() + 1);
    }

    dueDate = due.toISOString();
    hasTime = Boolean(timeMatch);

    const parts: string[] = [];
    if (dateMatch) {
      parts.push(dateMatch.label);
      spans.push(dateMatch.match);
    }
    if (timeMatch) {
      parts.push(lower.slice(timeMatch.match.start, timeMatch.match.end).trim());
      spans.push(timeMatch.match);
    }
    dateLabel = parts.join(" ");
  }

  const title = stripSpans(raw, spans);

  return {
    title,
    dueDate,
    hasTime,
    listToken: listToken ? listToken.name : null,
    dateLabel,
  };
}

export const QUICK_ADD_DEFAULT_DUE_HOUR = DEFAULT_DUE_HOUR;
